import { Alert, Box, FormGroup, RadioGroup, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colorTokens } from '../../app/designTokens';
import { USER_TIER_OPTIONS } from '../../utils/registerWizardModel';
import { RegisterSectionCard } from './RegisterSectionCard';
import { SelectableOptionCard } from './SelectableOptionCard';
import { TrialSummaryCard } from './TrialSummaryCard';
import { formatRegisterAmount } from './registerSurfaces';

const { semantic } = colorTokens;

function warningMessage(warning, t) {
  if (warning === 'no_modules_selected') {
    return t('pricing:register.noModulesSelected');
  }
  return warning;
}

/**
 * Step 1 — modules, user tier, billing period, add-on services, trial summary.
 * Keeps the H05H catalog contract: register-modules / register-module-{code} /
 * register-module-{code}-input.
 */
export function RegisterStepModules({
  catalogModules,
  catalogLoading,
  selectedModules,
  onToggleModule,
  userTierId,
  onTierChange,
  billingInterval,
  onBillingChange,
  smsNotificationsEnabled,
  onSmsChange,
  startMode,
  onStartModeChange,
  buyNowEnabled,
  preview,
  previewLoading,
  previewError,
  stepErrors,
  usersCount,
}) {
  const { t } = useTranslation(['onboarding', 'pricing', 'register', 'common']);
  const hasNoModules = selectedModules.length === 0;
  const annualMonthsCharged = preview?.annual_months_charged ?? 11;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems="flex-start"
      data-testid="register-step-modules"
    >
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <RegisterSectionCard
          title={t('pricing:register.selectModules')}
          helper={t('onboarding:register.wizard.modules_helper')}
          testId="register-modules-section"
        >
          {catalogLoading ? (
            <Typography sx={{ fontSize: 13, color: semantic.text.muted }}>
              {t('common:loading')}
            </Typography>
          ) : (
            <FormGroup
              data-testid="register-modules"
              sx={{
                display: 'grid',
                width: '100%',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.5,
                // FormGroup defaults to column flex; force grid for Figma card layout.
                flexDirection: 'unset',
                alignSelf: 'stretch',
              }}
            >
              {catalogModules.map((module) => {
                const code = module.public_code || module.canonical_code;
                return (
                  <SelectableOptionCard
                    key={module.canonical_code}
                    testId={`register-module-${code}`}
                    inputTestId={`register-module-${code}-input`}
                    selected={selectedModules.includes(code)}
                    onSelect={() => onToggleModule(code)}
                    title={module.name}
                    badge={code}
                    trailing={formatRegisterAmount(module.monthly_price, module.currency, t)}
                  />
                );
              })}
            </FormGroup>
          )}
          {stepErrors.modules ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('pricing:register.noModulesSelected')}
            </Alert>
          ) : null}
          {hasNoModules && !stepErrors.modules ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('pricing:register.noModulesSelected')}
            </Alert>
          ) : null}
        </RegisterSectionCard>

        <RegisterSectionCard
          title={t('pricing:register.userTier')}
          helper={t('onboarding:register.wizard.user_tier_helper')}
          testId="register-user-tiers"
        >
          <RadioGroup
            value={userTierId}
            onChange={onTierChange}
            aria-label={t('pricing:register.userTier')}
            sx={{ gap: 1 }}
          >
            {USER_TIER_OPTIONS.map((tier) => (
              <SelectableOptionCard
                key={tier.id}
                control="radio"
                value={tier.id}
                selected={userTierId === tier.id}
                testId={`register-user-tier-${tier.id}`}
                inputTestId={`register-user-tier-${tier.id}-input`}
                title={t(`onboarding:register.wizard.${tier.labelKey}`)}
              />
            ))}
          </RadioGroup>
        </RegisterSectionCard>

        <RegisterSectionCard
          title={t('pricing:pricing_preview.billing_interval')}
          helper={t('onboarding:register.wizard.billing_helper', { months: annualMonthsCharged })}
          testId="register-billing-period"
        >
          <RadioGroup
            value={billingInterval}
            onChange={onBillingChange}
            aria-label={t('pricing:pricing_preview.billing_interval')}
            sx={{ gap: 1 }}
          >
            <SelectableOptionCard
              control="radio"
              value="monthly"
              selected={billingInterval === 'monthly'}
              testId="register-billing-monthly"
              inputTestId="register-billing-monthly-input"
              title={t('pricing:pricing_preview.interval_monthly')}
            />
            <SelectableOptionCard
              control="radio"
              value="yearly"
              selected={billingInterval === 'yearly'}
              testId="register-billing-yearly"
              inputTestId="register-billing-yearly-input"
              title={t('pricing:pricing_preview.interval_yearly')}
              badge={annualMonthsCharged < 12
                ? t('onboarding:register.wizard.billing_yearly_badge', {
                  months: 12 - annualMonthsCharged,
                })
                : undefined}
              description={annualMonthsCharged < 12
                ? t('pricing:register.yearlyChargedMonths')
                : undefined}
            />
          </RadioGroup>
        </RegisterSectionCard>

        <RegisterSectionCard
          title={t('register:addons')}
          helper={t('onboarding:register.wizard.addons_helper')}
          testId="register-addons"
        >
          <SelectableOptionCard
            selected={smsNotificationsEnabled}
            onSelect={onSmsChange}
            testId="register-addon-sms"
            inputTestId="register-addon-sms-input"
            title={t('register:smsNotifications')}
            description={t('register:smsNotificationsHelp')}
          />

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, color: semantic.text.secondary, mb: 1 }}>
              {t('register:startMode')}
            </Typography>
            <RadioGroup
              value={startMode}
              onChange={onStartModeChange}
              aria-label={t('register:startMode')}
              sx={{ gap: 1 }}
            >
              <SelectableOptionCard
                control="radio"
                value="trial"
                selected={startMode === 'trial'}
                testId="register-start-mode-trial"
                inputTestId="register-start-mode-trial-input"
                title={t('register:startTrial')}
              />
              <SelectableOptionCard
                control="radio"
                value="buy_now"
                selected={startMode === 'buy_now'}
                disabled={!buyNowEnabled}
                testId="register-start-mode-buy-now"
                inputTestId="register-start-mode-buy-now-input"
                title={buyNowEnabled ? t('register:buyNow') : t('register:buyNowComingSoon')}
              />
            </RadioGroup>
          </Box>
        </RegisterSectionCard>

        {(preview?.warnings || []).map((warning) => (
          <Alert key={warning} severity="warning">{warningMessage(warning, t)}</Alert>
        ))}
      </Stack>

      <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
        <TrialSummaryCard
          preview={preview}
          loading={previewLoading}
          error={previewError}
          selectedModules={selectedModules}
          billingInterval={billingInterval}
          usersCount={usersCount}
          smsNotificationsEnabled={smsNotificationsEnabled}
          startMode={startMode}
        />
      </Box>
    </Stack>
  );
}

export default RegisterStepModules;
